import {
  addToast,
  Button,
  Dialogß,
  IconButton,
  Skeleton,
  SuccessToast,
} from '@otter-ai/den-components';
import { ZoomInIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { descriptionFormatRules } from '@/features/account-settings/shared/utils/text';
import { FormattedText } from '@/shared/components/FormattedText';
import { useSetupTwoFactorAuthAppMethod } from '@/shared/hooks/two-factor-authentication/useSetupTwoFactorAuthAppMethod';
import { openDialog } from '@/shared/modals/openDialog';
import { Security2FAType } from '@/shared/types/auth';
import { copyPlainTextToClipboard } from '@/shared/utils/clipboard';
import { AUTHENTICATOR_APP_LINKS } from '../constants';
import { dataTestIds } from '../dataTestIds';
import { StateEvent } from '../state-machine-config';
import { TwoFactorAuthStateLoading } from './TwoFactorAuthStateLoading';
import { VerifyCodeForm } from './VerifyCodeForm';

export function AuthAppSetupState(props: {
  [StateEvent.PICK_DIFFERENT_METHOD]: () => void;
  [StateEvent.FINISH_SETUP]: () => void;
}) {
  const t = useTranslations(
    'settings:security.twoFactorAuthStates.authAppSetup',
  );
  const { secret, isLoading, qrCodeDataUrl } = useSetupTwoFactorAuthAppMethod();

  if (isLoading) {
    return <TwoFactorAuthStateLoading />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grow font-semibold text-lg">
        {t('viaAuthenticationApp')}
      </div>
      <ol className="list-decimal pl-[1.5em] [&>li]:list-item flex flex-col gap-4">
        <li>
          <FormattedText>
            {t.rich(
              'installAuthenticatorStep',
              descriptionFormatRules({
                links: AUTHENTICATOR_APP_LINKS,
              }),
            )}
          </FormattedText>
        </li>
        <li>
          <FormattedText>
            {t.rich('openAppStep', descriptionFormatRules({}))}
          </FormattedText>
        </li>
        <li>
          <DisplayQrAndSecret qrCodeDataUrl={qrCodeDataUrl} secret={secret} />
        </li>
        <li>
          <VerifyCodeForm
            twoFactorAuthType={Security2FAType.AUTHENTICATION_APP}
            onFinishSetup={props[StateEvent.FINISH_SETUP]}
            onPickDifferentMethod={props[StateEvent.PICK_DIFFERENT_METHOD]}
          />
        </li>
      </ol>
    </div>
  );
}

function DisplayQrAndSecret({
  qrCodeDataUrl,
  secret,
}: {
  qrCodeDataUrl: string | null;
  secret: string | null;
}) {
  return (
    <div>
      <ScanQrCodeStep qrCodeDataUrl={qrCodeDataUrl} />
      <CopySecretKeyStep secret={secret} />
    </div>
  );
}

function ScanQrCodeStep({ qrCodeDataUrl }: { qrCodeDataUrl: string | null }) {
  const t = useTranslations(
    'settings:security.twoFactorAuthStates.authAppSetup',
  );

  const openFullScreenQrCode = () => {
    if (!qrCodeDataUrl) {
      return;
    }

    openDialog(async ({ done }) => (
      <Dialogß
        size="full"
        onOpenChange={() => done()}
        Header={<span />}
        className="h-screen"
        data-testid="qr-code-dialog"
      >
        <div className="h-full flex items-center justify-center">
          {/** biome-ignore lint/performance/noImgElement: Use img because it uses a blob URL */}
          <img
            src={qrCodeDataUrl}
            alt={t('qrCodeAltText')}
            className="w-full h-full block object-contain"
          />
        </div>
      </Dialogß>
    ));
  };
  return (
    <div>
      <FormattedText>
        {t.rich('scanQrCodeStep', descriptionFormatRules({}))}
      </FormattedText>
      {!qrCodeDataUrl && (
        <Skeleton className="max-w-[400px] w-full aspect-square block" />
      )}
      {qrCodeDataUrl && (
        <div className="flex items-start gap-2">
          {/** biome-ignore lint/performance/noImgElement: Use img because it uses a blob URL */}
          <img
            src={qrCodeDataUrl}
            alt={t('qrCodeAltText')}
            className="w-full max-w-[300px] sm:max-w-[400px] aspect-square block"
          />
          <IconButton
            className="hidden sm:flex"
            variant="outlineNeutral"
            version="legacy"
            onClick={openFullScreenQrCode}
          >
            <span className="sr-only">{t('zoomInButton')}</span>
            <ZoomInIcon />
          </IconButton>
        </div>
      )}
    </div>
  );
}

function CopySecretKeyStep({ secret }: { secret: string | null }) {
  const t = useTranslations(
    'settings:security.twoFactorAuthStates.authAppSetup',
  );

  return (
    <div>
      {' '}
      <FormattedText>
        {t.rich('orEnterManuallyStep', descriptionFormatRules({}))}
      </FormattedText>
      <div className="flex flex-col gap-2 sm:flex-row sm:gap-4 sm:items-center">
        <div
          className="py-2 px-3 tracking-[.05em] text-sm sm:text-lg rounded-sm border border-subtle break-all"
          data-testid="2fa-setup-auth-app-secret-key-input-field"
        >
          {secret}
        </div>
        <div className="flex-shrink-0">
          {secret && (
            <Button
              variant="outlineNeutral"
              version="legacy"
              onClick={() => {
                if (copyPlainTextToClipboard(secret)) {
                  addToast(
                    <SuccessToast description={t('secretKeyCopiedToast')} />,
                  );
                }
              }}
              className="w-full sm:w-auto"
              data-testid={dataTestIds.copySecretKeyButton}
            >
              {t('copyButton')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
